import React, { useEffect, useState, useRef } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Alert,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  IconButton,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { PhotoCamera, Visibility, VisibilityOff, ExpandMore } from '@mui/icons-material';
import Layout from '../../components/Layout';
import api from '../../services/api';
import { getDriverProfilePictureUrl } from '../../utils/s3Utils';

const DriverProfile = () => {
  const [profile, setProfile] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    phone_number: '',
    status: 'idle',
    current_vehicle: '',
    license_number: '',
    license_expiration_date: '',
    license_state: '',
    license_class: '',
  });
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const locationIntervalRef = useRef(null);
  const watchPositionIdRef = useRef(null);
  const [hasVehicle, setHasVehicle] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchAvailableVehicles();
    fetchOrganization();
    return () => {
      // Cleanup interval on unmount
      if (locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
      }
    };
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/drivers/profile/');
      setProfile(response.data);
      const vehicleId = response.data.current_vehicle || response.data.current_vehicle_info?.id || '';
      setHasVehicle(!!vehicleId);
      setFormData({
        name: response.data.name || '',
        phone_number: response.data.phone_number || '',
        status: response.data.status || 'idle',
        current_vehicle: vehicleId,
        license_number: response.data.license_number || '',
        license_expiration_date: response.data.license_expiration_date || '',
        license_state: response.data.license_state || '',
        license_class: response.data.license_class || '',
      });
      // Set profile photo preview from S3 URL
      // Backend returns the direct S3 URL: https://bucket.s3.region.amazonaws.com/driver/{driver_id}/profile_picture.jpg
      console.log('Profile picture URL from API:', response.data.profile_photo);
      console.log('Driver ID:', response.data.id);
      if (response.data.profile_photo) {
        // Backend provides the S3 URL directly
        console.log('Setting profile photo preview to:', response.data.profile_photo);
        setProfilePhotoPreview(response.data.profile_photo);
      } else {
        console.log('No profile photo URL provided by backend');
        setProfilePhotoPreview(null);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      // If profile doesn't exist, show form to create it
      if (error.response?.status === 404) {
        setMessage('Please complete your profile information below.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableVehicles = async () => {
    try {
      const response = await api.get('/drivers/available_vehicles/');
      setAvailableVehicles(response.data || []);
    } catch (error) {
      console.error('Error fetching available vehicles:', error);
      // Fallback: try to get all vehicles
      try {
        const vehiclesRes = await api.get('/vehicles/');
        setAvailableVehicles(vehiclesRes.data || []);
      } catch (fallbackError) {
        console.error('Error fetching vehicles:', fallbackError);
      }
    }
  };

  const fetchOrganization = async () => {
    try {
      const response = await api.get('/organizations/organizations/profile/');
      setOrganization(response.data);
    } catch (error) {
      console.error('Error fetching organization:', error);
      // Organization might not exist yet, that's okay
      if (error.response?.status !== 404) {
        console.error('Unexpected error fetching organization:', error);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Update hasVehicle state when vehicle assignment changes
    if (name === 'current_vehicle') {
      setHasVehicle(!!value);
    }
    
    // If status changes to 'not_working' or 'license_suspended', clear vehicle selection
    if (name === 'status' && (value === 'not_working' || value === 'license_suspended')) {
      setFormData(prev => ({
        ...prev,
        status: value,
        current_vehicle: '', // Clear vehicle when status is 'not_working' or 'license_suspended'
      }));
      setHasVehicle(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setMessage('Please select an image file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setMessage('Image size must be less than 5MB');
        return;
      }
      setProfilePhoto(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      // Use PUT if profile exists, POST if it doesn't
      const method = profile ? 'put' : 'post';
      
      // Prepare FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('phone_number', formData.phone_number);
      formDataToSend.append('status', formData.status);
      if (formData.current_vehicle) {
        formDataToSend.append('current_vehicle', formData.current_vehicle);
      } else {
        formDataToSend.append('current_vehicle', '');
      }
      // Add license information (required fields)
      formDataToSend.append('license_number', formData.license_number || '');
      formDataToSend.append('license_expiration_date', formData.license_expiration_date || '');
      formDataToSend.append('license_state', formData.license_state || '');
      formDataToSend.append('license_class', formData.license_class || '');
      
      // Add profile photo if selected
      if (profilePhoto) {
        formDataToSend.append('profile_photo', profilePhoto);
      }
      
      const response = await api[method]('/drivers/profile/', formDataToSend);
      
      setMessage('Profile saved successfully');
      // Clear photo selection after successful upload
      setProfilePhoto(null);
      
      // Refresh profile to get updated data including S3 URL for profile photo
      await fetchProfile();
      // Refresh available vehicles in case assignments changed
      fetchAvailableVehicles();
      // Update hasVehicle state based on new profile data
      const vehicleId = response.data?.current_vehicle || response.data?.current_vehicle_info?.id || '';
      setHasVehicle(!!vehicleId);
    } catch (error) {
      console.error('Error saving profile:', error);
      let errorMsg = 'Error saving profile. Please check your input.';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMsg = error.response.data;
        } else if (error.response.data.error) {
          errorMsg = error.response.data.error;
        } else if (error.response.data.message) {
          errorMsg = error.response.data.message;
        } else if (typeof error.response.data === 'object') {
          // Handle validation errors
          const errors = Object.values(error.response.data).flat();
          errorMsg = errors.join(', ') || errorMsg;
        }
      }
      
      setMessage(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
    setPasswordMessage('');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordMessage('');

    // Validation
    if (!passwordData.old_password || !passwordData.new_password || !passwordData.confirm_password) {
      setPasswordMessage('All fields are required');
      return;
    }

    if (passwordData.new_password.length < 8) {
      setPasswordMessage('New password must be at least 8 characters long');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordMessage('New passwords do not match');
      return;
    }

    setChangingPassword(true);

    try {
      await api.post('/auth/password/change/', {
        old_password: passwordData.old_password,
        new_password: passwordData.new_password,
      });
      setPasswordMessage('Password changed successfully');
      setPasswordData({
        old_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (error) {
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      'Failed to change password. Please check your current password.';
      setPasswordMessage(errorMsg);
    } finally {
      setChangingPassword(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field],
    });
  };

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    });
  };

  const startWatchPosition = () => {
    if (!navigator.geolocation || !hasVehicle) {
      return;
    }

    // Stop any existing watch
    if (watchPositionIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchPositionIdRef.current);
    }

    // Start watching position for continuous updates
    watchPositionIdRef.current = navigator.geolocation.watchPosition(
      async (position) => {
        try {
          await api.post('/drivers/update_location/', {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        } catch (error) {
          console.error('Error updating location from watchPosition:', error);
        }
      },
      (error) => {
        console.error('Error watching position:', error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 5000, // Accept cached position up to 5 seconds old
      }
    );
  };

  const stopWatchPosition = () => {
    if (watchPositionIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchPositionIdRef.current);
      watchPositionIdRef.current = null;
    }
  };

  const updateLocation = async () => {
    if (!hasVehicle) {
      return;
    }

    try {
      const location = await getCurrentLocation();
      await api.post('/drivers/update_location/', {
        latitude: location.latitude,
        longitude: location.longitude,
      });
    } catch (error) {
      // Silently fail - don't show errors for background location updates
      console.error('Error updating location:', error);
    }
  };

  // Automatically start location tracking when vehicle is assigned
  useEffect(() => {
    if (hasVehicle) {
      // Start watchPosition for continuous background tracking
      startWatchPosition();

      // Also use interval as fallback (works even when watchPosition might be throttled)
      if (!locationIntervalRef.current) {
        // Update immediately
        updateLocation();

        // Then update every 10 seconds
        locationIntervalRef.current = setInterval(() => {
          updateLocation();
        }, 10000); // 10 seconds
      }
    } else {
      // Stop tracking if vehicle is unassigned
      stopWatchPosition();
      if (locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
        locationIntervalRef.current = null;
      }
    }
    
    // Cleanup on unmount or when hasVehicle changes
    return () => {
      stopWatchPosition();
      if (locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
        locationIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasVehicle]);

  // Handle page visibility changes to ensure tracking continues in background
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is in background - watchPosition should continue, but ensure interval is running
        if (hasVehicle && !locationIntervalRef.current) {
          locationIntervalRef.current = setInterval(() => {
            updateLocation();
          }, 10000);
        }
      } else {
        // Page is visible again - ensure watchPosition is active
        if (hasVehicle) {
          startWatchPosition();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [hasVehicle]);

  if (loading) {
    return (
      <Layout>
        <Container>Loading...</Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <Typography variant="h4" gutterBottom>
          Driver Profile
        </Typography>
        <Paper sx={{ p: 3, mt: 3 }}>
          {message && (
            <Alert 
              severity={message.includes('success') ? 'success' : message.includes('Please') ? 'info' : 'error'} 
              sx={{ mb: 2 }}
            >
              {message}
            </Alert>
          )}
          {!profile && !loading && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Complete your profile to start using the service.
            </Alert>
          )}
          
          {profile && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>Current Profile Information</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar
                    src={profilePhotoPreview || profile.profile_photo || getDriverProfilePictureUrl(profile.id, profile.profile_photo) || undefined}
                    alt={profile.name || 'Driver'}
                    sx={{ width: 100, height: 100 }}
                  >
                    {!(profilePhotoPreview || profile.profile_photo || getDriverProfilePictureUrl(profile.id, profile.profile_photo)) && (profile.name?.charAt(0)?.toUpperCase() || 'D')}
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{profile.name || 'Not set'}</Typography>
                    <Typography variant="body2" color="textSecondary">{profile.email || 'N/A'}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Email</Typography>
                  <Typography variant="body1">{profile.email || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Name</Typography>
                  <Typography variant="body1">{profile.name || 'Not set'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Phone Number</Typography>
                  <Typography variant="body1">{profile.phone_number || 'Not set'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Status</Typography>
                  <Typography variant="body1" textTransform="capitalize">
                    {profile.status || 'Not set'}
                  </Typography>
                </Grid>
                {profile.current_vehicle_info && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Current Vehicle</Typography>
                    <Typography variant="body1">
                      {profile.current_vehicle_info.display || 'Not assigned'}
                    </Typography>
                  </Grid>
                )}
                {profile.current_job_info && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">Current Job</Typography>
                    <Typography variant="body1">
                      {profile.current_job_info.job_location} (
                      {profile.current_job_info.diesel_quantity > 0 && `${profile.current_job_info.diesel_quantity}L Diesel`}
                      {profile.current_job_info.diesel_quantity > 0 && profile.current_job_info.def_quantity > 0 && ' / '}
                      {profile.current_job_info.def_quantity > 0 && `${profile.current_job_info.def_quantity}L DEF`}
                      {(!profile.current_job_info.diesel_quantity || profile.current_job_info.diesel_quantity === 0) && (!profile.current_job_info.def_quantity || profile.current_job_info.def_quantity === 0) && '0L'}
                      )
                    </Typography>
                  </Grid>
                )}
                {(profile.license_number || profile.license_expiration_date || profile.license_state || profile.license_class) && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Driver's License Information</Typography>
                    </Grid>
                    {profile.license_number && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">License Number</Typography>
                        <Typography variant="body1">{profile.license_number}</Typography>
                      </Grid>
                    )}
                    {profile.license_state && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">License State</Typography>
                        <Typography variant="body1">{profile.license_state}</Typography>
                      </Grid>
                    )}
                    {profile.license_class && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">License Class</Typography>
                        <Typography variant="body1">{profile.license_class}</Typography>
                      </Grid>
                    )}
                    {profile.license_expiration_date && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Expiration Date</Typography>
                        <Typography variant="body1">
                          {new Date(profile.license_expiration_date).toLocaleDateString()}
                        </Typography>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </Box>
          )}

          {organization && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>Organization Details</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Organization Name
                  </Typography>
                  <Typography variant="body1">{organization.name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Email
                  </Typography>
                  <Typography variant="body1">{organization.email || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Phone
                  </Typography>
                  <Typography variant="body1">{organization.phone || 'Not provided'}</Typography>
                </Grid>
                {organization.tax_id && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      Tax ID / EIN
                    </Typography>
                    <Typography variant="body1">{organization.tax_id}</Typography>
                  </Grid>
                )}
                {(organization.address || organization.city || organization.state || organization.zip_code) && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      Address
                    </Typography>
                    <Typography variant="body1">
                      {[
                        organization.address,
                        organization.city,
                        organization.state,
                        organization.zip_code
                      ].filter(Boolean).join(', ') || 'Not provided'}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
            {profile ? 'Update Profile' : 'Create Profile'}
          </Typography>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar
                    src={profilePhotoPreview || profile?.profile_photo || getDriverProfilePictureUrl(profile?.id, profile?.profile_photo) || undefined}
                    alt={formData.name || 'Driver'}
                    sx={{ width: 120, height: 120 }}
                  >
                    {!(profilePhotoPreview || profile?.profile_photo || getDriverProfilePictureUrl(profile?.id, profile?.profile_photo)) && (formData.name?.charAt(0)?.toUpperCase() || 'D')}
                  </Avatar>
                  <Box>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="profile-photo-upload"
                      type="file"
                      onChange={handlePhotoChange}
                    />
                    <label htmlFor="profile-photo-upload">
                      <IconButton color="primary" component="span">
                        <PhotoCamera />
                      </IconButton>
                    </label>
                    <Typography variant="caption" display="block" textAlign="center">
                      {profilePhoto ? 'New photo selected' : 'Click to upload profile photo'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    label="Status"
                  >
                    <MenuItem value="idle">Idle</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="refueling">Refueling</MenuItem>
                    <MenuItem value="not_working">Not Working</MenuItem>
                    <MenuItem value="license_suspended">License Suspended</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Current Vehicle</InputLabel>
                  <Select
                    name="current_vehicle"
                    value={formData.current_vehicle}
                    onChange={handleChange}
                    label="Current Vehicle"
                    disabled={
                      // Allow unassignment if vehicle is idle, otherwise check driver status and job
                      (formData.current_vehicle && 
                       profile?.current_vehicle_info?.status !== 'idle' && 
                       (formData.status !== 'idle' || profile?.current_job)) ||
                      formData.status === 'not_working' || 
                      formData.status === 'license_suspended'
                    }
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {availableVehicles.map((vehicle) => (
                      <MenuItem key={vehicle.id} value={vehicle.id}>
                        {vehicle.make} {vehicle.model} ({vehicle.license_plate})
                      </MenuItem>
                    ))}
                  </Select>
                  {(formData.status === 'not_working' || formData.status === 'license_suspended') && (
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                      Vehicle will be unassigned when status is "{formData.status === 'not_working' ? 'Not Working' : 'License Suspended'}"
                    </Typography>
                  )}
                  {formData.current_vehicle && profile?.current_vehicle_info?.status === 'idle' && (
                    <Typography variant="caption" color="success.main" sx={{ mt: 0.5 }}>
                      Vehicle is idle - you can unassign it
                    </Typography>
                  )}
                  {formData.current_vehicle && profile?.current_vehicle_info?.status !== 'idle' && formData.status !== 'idle' && (
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                      Can only change vehicle when driver status is idle and vehicle is idle
                    </Typography>
                  )}
                  {formData.current_vehicle && profile?.current_vehicle_info?.status !== 'idle' && profile?.current_job && (
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                      Cannot change vehicle when assigned to a job. Vehicle must be idle to unassign.
                    </Typography>
                  )}
                  {!formData.current_vehicle && formData.status !== 'idle' && (
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                      Can only assign vehicle when status is idle
                    </Typography>
                  )}
                  {!formData.current_vehicle && formData.status === 'idle' && !profile?.current_job && (
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                      Select a vehicle to assign (only idle vehicles are available)
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Driver's License Information</Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="License Number"
                  name="license_number"
                  value={formData.license_number}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>License State</InputLabel>
                  <Select
                    name="license_state"
                    value={formData.license_state}
                    onChange={handleChange}
                    label="License State"
                  >
                    <MenuItem value="">
                      <em>Select State</em>
                    </MenuItem>
                    <MenuItem value="AL">AL - Alabama</MenuItem>
                    <MenuItem value="AK">AK - Alaska</MenuItem>
                    <MenuItem value="AZ">AZ - Arizona</MenuItem>
                    <MenuItem value="AR">AR - Arkansas</MenuItem>
                    <MenuItem value="CA">CA - California</MenuItem>
                    <MenuItem value="CO">CO - Colorado</MenuItem>
                    <MenuItem value="CT">CT - Connecticut</MenuItem>
                    <MenuItem value="DE">DE - Delaware</MenuItem>
                    <MenuItem value="FL">FL - Florida</MenuItem>
                    <MenuItem value="GA">GA - Georgia</MenuItem>
                    <MenuItem value="HI">HI - Hawaii</MenuItem>
                    <MenuItem value="ID">ID - Idaho</MenuItem>
                    <MenuItem value="IL">IL - Illinois</MenuItem>
                    <MenuItem value="IN">IN - Indiana</MenuItem>
                    <MenuItem value="IA">IA - Iowa</MenuItem>
                    <MenuItem value="KS">KS - Kansas</MenuItem>
                    <MenuItem value="KY">KY - Kentucky</MenuItem>
                    <MenuItem value="LA">LA - Louisiana</MenuItem>
                    <MenuItem value="ME">ME - Maine</MenuItem>
                    <MenuItem value="MD">MD - Maryland</MenuItem>
                    <MenuItem value="MA">MA - Massachusetts</MenuItem>
                    <MenuItem value="MI">MI - Michigan</MenuItem>
                    <MenuItem value="MN">MN - Minnesota</MenuItem>
                    <MenuItem value="MS">MS - Mississippi</MenuItem>
                    <MenuItem value="MO">MO - Missouri</MenuItem>
                    <MenuItem value="MT">MT - Montana</MenuItem>
                    <MenuItem value="NE">NE - Nebraska</MenuItem>
                    <MenuItem value="NV">NV - Nevada</MenuItem>
                    <MenuItem value="NH">NH - New Hampshire</MenuItem>
                    <MenuItem value="NJ">NJ - New Jersey</MenuItem>
                    <MenuItem value="NM">NM - New Mexico</MenuItem>
                    <MenuItem value="NY">NY - New York</MenuItem>
                    <MenuItem value="NC">NC - North Carolina</MenuItem>
                    <MenuItem value="ND">ND - North Dakota</MenuItem>
                    <MenuItem value="OH">OH - Ohio</MenuItem>
                    <MenuItem value="OK">OK - Oklahoma</MenuItem>
                    <MenuItem value="OR">OR - Oregon</MenuItem>
                    <MenuItem value="PA">PA - Pennsylvania</MenuItem>
                    <MenuItem value="RI">RI - Rhode Island</MenuItem>
                    <MenuItem value="SC">SC - South Carolina</MenuItem>
                    <MenuItem value="SD">SD - South Dakota</MenuItem>
                    <MenuItem value="TN">TN - Tennessee</MenuItem>
                    <MenuItem value="TX">TX - Texas</MenuItem>
                    <MenuItem value="UT">UT - Utah</MenuItem>
                    <MenuItem value="VT">VT - Vermont</MenuItem>
                    <MenuItem value="VA">VA - Virginia</MenuItem>
                    <MenuItem value="WA">WA - Washington</MenuItem>
                    <MenuItem value="WV">WV - West Virginia</MenuItem>
                    <MenuItem value="WI">WI - Wisconsin</MenuItem>
                    <MenuItem value="WY">WY - Wyoming</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="License Class"
                  name="license_class"
                  value={formData.license_class}
                  onChange={handleChange}
                  placeholder="e.g., Class A, B, C, CDL"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="License Expiration Date"
                  name="license_expiration_date"
                  type="date"
                  value={formData.license_expiration_date}
                  onChange={handleChange}
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  size="large"
                >
                  {saving ? 'Saving...' : profile ? 'Update Profile' : 'Create Profile'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Password Change Section */}
        <Accordion sx={{ mt: 3 }}>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls="password-change-content"
            id="password-change-header"
          >
            <Typography variant="h6">Change Password</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {passwordMessage && (
              <Alert 
                severity={passwordMessage.includes('success') ? 'success' : 'error'} 
                sx={{ mb: 2 }}
              >
                {passwordMessage}
              </Alert>
            )}

            <Box component="form" onSubmit={handlePasswordSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Current Password"
                  name="old_password"
                  type={showPasswords.old ? 'text' : 'password'}
                  value={passwordData.old_password}
                  onChange={handlePasswordChange}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => togglePasswordVisibility('old')}
                          edge="end"
                        >
                          {showPasswords.old ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="New Password"
                  name="new_password"
                  type={showPasswords.new ? 'text' : 'password'}
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  required
                  helperText="Must be at least 8 characters long"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => togglePasswordVisibility('new')}
                          edge="end"
                        >
                          {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  name="confirm_password"
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={passwordData.confirm_password}
                  onChange={handlePasswordChange}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => togglePasswordVisibility('confirm')}
                          edge="end"
                        >
                          {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={changingPassword}
                  size="large"
                >
                  {changingPassword ? 'Changing Password...' : 'Change Password'}
                </Button>
              </Grid>
            </Grid>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Container>
    </Layout>
  );
};

export default DriverProfile;



